import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, call } from '@/lib/api';
import { isKycDenied } from '@/lib/kycAccess';
import { queryClient } from '@/lib/query';
import { referringAgentKeys } from '@/models/referringAgents';

export type TContact = {
  id: string;
  name: string;
  column_values: {
    value: null | string;
    text: null | string;
    column: {
      id: string;
      title: string;
    };
  }[];
  subitems: [];
};

export const contactKeys = {
  all: ['contacts'] as const,
  detail: (id: string) => [...contactKeys.all, 'detail', id] as const,
};

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: contactKeys.detail(id!),
    queryFn: async () =>
      call<TContact>('monday/contact', {
        json: { pulseId: id },
      }).then((res) => res.json()),
    enabled: !!id,
    retry: false,
    throwOnError: false,
  });
}

// BO-native local Contacts (separate from the Monday-backed `TContact` above).

export type ContactEventKind =
  | 'created'
  | 'owner_changed'
  | 'field_updated'
  | 'commented'
  | 'converted_from_lead'
  | 'qualified'
  | 'access_requested';

export type Contact = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  telegram: string | null;
  sourceLeadId: number | null;
  referringAgentId: number | null;
  referringAgentName?: string | null;
  accountId: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  memberUserIds?: number[];
  members?: ContactMemberSummary[];
  origin?: string | null;
  mondayItemId?: string | null;
  mondayOwners?: string[];
};

export type ContactMemberSummary = {
  userId: number;
  email: string;
  name: string | null;
  picture?: string | null;
};

export type ContactMember = ContactMemberSummary & {
  addedBy: number | null;
  addedAt: string;
};

export type ContactComment = {
  id: number;
  contactId: number;
  authorUserId: number | null;
  authorEmail: string | null;
  body: string;
  createdAt: string;
};

export type ContactEvent = {
  id: number;
  contactId: number;
  actorUserId: number | null;
  kind: ContactEventKind;
  payload: Record<string, unknown>;
  createdAt: string;
  actorEmail?: string | null;
  actorName?: string | null;
};

export type ContactAssignee = {
  id: number;
  email: string;
  info?: { name?: string; picture?: string } | null;
};

export type ContactListFilters = {
  ownerUserId?: number[];
  search?: string;
  view?: 'mine' | 'unassigned' | 'all';
  page?: number;
  pageSize?: number;
  sort?: 'createdAt' | 'updatedAt';
  order?: 'asc' | 'desc';
  offerable?: boolean;
};

export type ContactListResponse = {
  rows: Contact[];
  total: number;
  page: number;
  pageSize: number;
};

export type ContactRequest = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  status: string;
  source: string;
  assigneeUserId: number | null;
  assigneeEmail: string | null;
  assigneeName: string | null;
  duplicateOfLeadId: number | null;
  createdAt: string;
};

export type ContactDetailsResponse = {
  contact: Contact;
  members: ContactMember[];
  comments: ContactComment[];
  events: ContactEvent[];
  sourceLead: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    status: string;
    createdAt: string;
  } | null;
  leads: ContactRequest[];
  offers: {
    id: number;
    uuid: string;
    title: string;
    accepted: boolean;
    acceptedAt: string | null;
    createdAt: string;
  }[];
  account: { id: number; name: string } | null;
  importNotes?: ContactImportNote[];
};

export type EmailHolder = {
  type: 'contact' | 'lead';
  id: number;
  name: string | null;
  accountName: string | null;
  visible: boolean;
};

export type ContactImportNote =
  | { kind: 'email_taken'; email: string; holder: EmailHolder | null }
  | {
      kind: 'owner_not_in_bo';
      column: string | null;
      name: string | null;
      email: string | null;
    };

export class EmailTakenError extends Error {
  readonly handled = true;
  constructor(
    readonly contactId: number,
    readonly holderName: string | null,
  ) {
    super('The email belongs to another contact');
    this.name = 'EmailTakenError';
  }
}

function asEmailTaken(e: unknown): EmailTakenError | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  try {
    const body = JSON.parse(e.body);
    if (body?.code !== 'email_taken') return null;
    return new EmailTakenError(Number(body.contactId), body.name ?? null);
  } catch {
    return null;
  }
}

export const localContactKeys = {
  all: ['local-contacts'] as const,
  lists: () => [...localContactKeys.all, 'list'] as const,
  list: (f: ContactListFilters) => [...localContactKeys.lists(), f] as const,
  details: () => [...localContactKeys.all, 'detail'] as const,
  detail: (id: number) => [...localContactKeys.details(), Number(id)] as const,
  assignees: ['local-contacts', 'assignees'] as const,
};

const callGet = <T>(endpoint: string, json?: any): Promise<T> =>
  call<T>(endpoint, { method: 'GET', json }).then((r) => r.json());

const callPost = <T>(endpoint: string, json?: any): Promise<T> =>
  call<T>(endpoint, { json }).then((r) => r.json());

function serializeFilters(f: ContactListFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.ownerUserId?.length) out.ownerUserId = f.ownerUserId.join(',');
  if (f.search) out.search = f.search;
  if (f.view) out.view = f.view;
  if (f.page) out.page = f.page;
  if (f.pageSize) out.pageSize = f.pageSize;
  if (f.sort) out.sort = f.sort;
  if (f.order) out.order = f.order;
  if (f.offerable) out.offerable = true;
  return out;
}

export function useContactsList(filters: ContactListFilters) {
  return useQuery({
    queryKey: localContactKeys.list(filters),
    queryFn: () =>
      callGet<ContactListResponse>('contacts/list', serializeFilters(filters)),
  });
}

export function useContactDetails(id: number | null | undefined) {
  return useQuery({
    enabled: id != null,
    queryKey: localContactKeys.detail(id ?? 0),
    queryFn: () => callGet<ContactDetailsResponse>('contacts/details', { id }),
  });
}

export function useContactAssignees() {
  return useQuery({
    queryKey: localContactKeys.assignees,
    queryFn: () => callGet<{ users: ContactAssignee[] }>('contacts/assignees'),
  });
}

const invalidateLists = () =>
  queryClient.invalidateQueries({ queryKey: localContactKeys.lists() });
const invalidateDetail = (id: number) => {
  queryClient.invalidateQueries({ queryKey: localContactKeys.detail(id) });
  queryClient.invalidateQueries({
    queryKey: [...localContactKeys.details(), 'lead'],
  });
  queryClient.invalidateQueries({
    queryKey: [...localContactKeys.details(), 'pulse'],
  });
};

export function useCreateContact() {
  return useMutation({
    mutationFn: (body: Partial<Contact>) =>
      callPost<{ contact: Contact }>('contacts/create', body),
    onSuccess: invalidateLists,
  });
}

export function useUpdateContact() {
  return useMutation({
    mutationFn: (body: Partial<Contact> & { id: number }) =>
      callPost<{ contact: Contact }>('contacts/update', body).catch((e) => {
        throw asEmailTaken(e) ?? e;
      }),
    onSuccess: (_d, vars) => {
      invalidateLists();
      invalidateDetail(vars.id);
      if ('referringAgentId' in vars) {
        queryClient.invalidateQueries({ queryKey: referringAgentKeys.all });
      }
    },
  });
}

export function useAddContactMember() {
  return useMutation({
    mutationFn: (body: { contactId: number; userId: number }) =>
      callPost<{ ok: true }>('contacts/add-member', body),
    onSuccess: (_d, vars) => {
      invalidateLists();
      invalidateDetail(vars.contactId);
    },
  });
}

export function useRemoveContactMember() {
  return useMutation({
    mutationFn: (body: { contactId: number; userId: number }) =>
      callPost<{ ok: true }>('contacts/remove-member', body),
    onSuccess: (_d, vars) => {
      invalidateLists();
      invalidateDetail(vars.contactId);
    },
  });
}

export function useAddContactComment() {
  return useMutation({
    mutationFn: (body: { contactId: number; body: string }) =>
      callPost<{ comment: ContactComment }>('contacts/comment', body),
    onSuccess: (_d, vars) => invalidateDetail(vars.contactId),
  });
}

export function useDeleteContact() {
  return useMutation({
    mutationFn: (id: number) =>
      callPost<{ ok: true }>('contacts/delete', { id }),
    onSuccess: invalidateLists,
  });
}

// Legacy Monday-pulse KYC link management.

export type MondayContactKyc = {
  pulseId: string;
  applicantId: string | null;
  sumsubUrl: string | null;
  sumsubUrlExpiresAt: string | null;
};

export const mondayContactKycKeys = {
  all: ['monday-contact-kyc'] as const,
  detail: (pulseId: string) =>
    [...mondayContactKycKeys.all, 'detail', pulseId] as const,
};

export function useMondayContactKyc(pulseId: string | undefined) {
  return useQuery({
    enabled: !!pulseId,
    queryKey: mondayContactKycKeys.detail(pulseId ?? ''),
    queryFn: () =>
      callPost<{
        info: MondayContactKyc[];
        documents: import('@/types').Document[];
      }>('contact/kyc', { pulseId }),
    retry: false,
  });
}

export function useGenerateContactKycLink() {
  return useMutation({
    mutationFn: async (pulseId: string) => {
      try {
        return await callPost<
          Pick<MondayContactKyc, 'sumsubUrl' | 'sumsubUrlExpiresAt'>
        >('contact/kyc-generate-link', { pulseId });
      } catch (e) {
        if (isKycDenied(e)) throw e;
        // No contact_kyc row yet — initialise (verifies pulse exists in Monday) and retry.
        await callPost<void>('contact/kyc-init', { pulseId });
        return callPost<
          Pick<MondayContactKyc, 'sumsubUrl' | 'sumsubUrlExpiresAt'>
        >('contact/kyc-generate-link', { pulseId });
      }
    },
    onSuccess: (_d, pulseId) =>
      queryClient.invalidateQueries({
        queryKey: mondayContactKycKeys.detail(pulseId),
      }),
  });
}
